import React, { createContext, useContext, useState, useEffect, useCallback, useMemo } from 'react';
import { translations } from '../i18n/translations';

const LanguageContext = createContext();

export const useLanguage = () => {
  const context = useContext(LanguageContext);
  if (!context) {
    throw new Error('useLanguage must be used within a LanguageProvider');
  }
  return context;
};

export const LanguageProvider = ({ children }) => {
  const [language, setLanguage] = useState(() => {
    const saved = localStorage.getItem('language');
    return saved || 'fr';
  });

  useEffect(() => {
    localStorage.setItem('language', language);
    // Synchronise l'attribut `<html lang>` avec la langue applicative pour
    // que Chrome/Safari/lecteurs d'écran comprennent que le contenu est
    // dans la bonne langue et n'activent PAS l'auto-traduction du
    // navigateur (bug utilisateur 2026-07-07 : Chrome forçait la re-
    // traduction FR→FR mangled quand `<html lang="fr">` était figé).
    try {
      const iso = { fr: 'fr-CA', en: 'en-US', es: 'es-ES' }[language] || 'fr';
      document.documentElement.lang = iso;
    } catch { /* no-op */ }
  }, [language]);

  const t = useCallback((key) => {
    const keys = key.split('.');
    let value = translations[language];
    
    for (const k of keys) {
      if (value && typeof value === 'object') {
        value = value[k];
      } else {
        return key;
      }
    }
    
    return value || key;
  }, [language]);

  const changeLanguage = useCallback((newLanguage) => {
    if (translations[newLanguage]) {
      setLanguage(newLanguage);
    }
  }, []);

  const contextValue = useMemo(
    () => ({ language, changeLanguage, t }),
    [language, changeLanguage, t]
  );

  return (
    <LanguageContext.Provider value={contextValue}>
      {children}
    </LanguageContext.Provider>
  );
};